import SimpleDelegator from '../simple_delegator';

class StrictRequestError extends Error {
  constructor(request, response) {
    super(`Request failed with status ${response.statusCode} (${response.response.statusText})`);
    this.name = 'StrictRequestError';
    this.request = request;
    this.response = response;
  }
}

/**
 * A decorator that rejects `perform()` for any response outside the 2xx/3xx range.
 *
 * FetchRequest resolves for every completed HTTP response, even 4xx/5xx — callers
 * must check `response.ok` themselves to detect failure. This decorator does that
 * check once, rejecting with a StrictRequestError (carrying the original request and
 * response, so status/headers/body are still reachable) instead of resolving.
 * Network-level failures (no response at all) still reject as-is, unchanged.
 *
 * @example
 *   const request = new StrictRequest(new FetchRequest('put', '/boards/1', { body }));
 *   request.perform().then(handleSuccess, handleFailure); // rejects on 4xx/5xx
 */
class StrictRequest extends SimpleDelegator {
  /**
   * @return {Promise<FetchResponse, StrictRequestError>}
   *   resolves with `FetchResponse` (see @rails/request.js) or rejects with {@link StrictRequestError}
   */
  perform() {
    return new Promise((resolve, reject) => {
      this.__target__
        .perform()
        .then((response) => {
          if (response.statusCode < 200 || response.statusCode >= 400) {
            reject(new StrictRequestError(this, response));
            return;
          }

          resolve(response);
        })
        .catch(reject);
    });
  }
}

StrictRequest.Error = StrictRequestError;

export default StrictRequest;
